import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus, Users } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Skeleton } from "./ui/skeleton";
import { EmpCode, StaffAvatar, staffLabelText } from "./StaffAvatar";
import { api, errMsg } from "../lib/api";

/**
 * Phase 96 — "Assign" on an Unassigned Wards row.
 * Opens a picker of the tax collectors who belong to the ward's zone (via their supervisor's zones;
 * collectors without zone restrictions are listed too). Choosing one adds the ward to their
 * territory AND assigns the ward's unassigned properties to them in one call — no page change.
 */
const COVERS_META = {
  whole: { label: "already covers this ward", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  partial: { label: "covers some mohallas → whole ward", cls: "bg-orange-50 text-orange-800 border-orange-200" },
};

const plural = (n, one, many) => `${Number(n || 0).toLocaleString("en-IN")} ${n === 1 ? one : many}`;

export function AssignWardPopover({ code, row, onAssigned, dense = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);      // { ward, unassigned_count, collectors }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const k = row.ward_id;

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/t/${code}/assignments/unassigned-wards/${row.ward_id}/collectors`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, code, row.ward_id]);

  const assign = async (c) => {
    if (busyId) return;
    setBusyId(c.id);
    try {
      const r = await api.post(`/t/${code}/assignments/unassigned-wards/${row.ward_id}/assign`, { collector_id: c.id });
      const d = r.data;
      toast.success(`${row.ward_name} assigned to ${staffLabelText(d.collector.name, d.collector.employee_id)}`, {
        description: `${plural(d.assigned, "property", "properties")} assigned`
          + (d.remaining ? ` • ${plural(d.remaining, "property", "properties")} still unassigned` : "")
          + (d.collector.previous_coverage === "partial" ? " • upgraded to whole ward" : ""),
      });
      setOpen(false);
      onAssigned?.(d);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const collectors = data?.collectors || [];
  const zoneLabel = row.zone_name || data?.ward?.zone_name;

  return (
    <Popover open={open} onOpenChange={(o) => { if (!busyId) setOpen(o); }}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className={`${dense ? "h-7" : "h-8"} border-primary/40 text-primary hover:bg-primary/5 hover:text-primary`}
          data-testid={`unassigned-ward-assign-${k}`} aria-label={`Assign ${row.ward_name} to a tax collector`}>
          <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 overflow-hidden" data-testid={`unassigned-ward-assign-popover-${k}`}>
        <div className="px-3 py-2.5 border-b bg-muted/40">
          <p className="text-sm font-medium leading-tight" data-testid={`unassigned-ward-assign-title-${k}`}>
            Assign {row.ward_name}{zoneLabel ? <span className="text-muted-foreground font-normal"> · {zoneLabel}</span> : null}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {data ? `${plural(data.unassigned_count, "unassigned property", "unassigned properties")} · ` : ""}
            adds the whole ward to the collector's territory and assigns them right away.
          </p>
        </div>
        {loading && !data ? (
          <div className="p-3 space-y-2" data-testid={`unassigned-ward-assign-loading-${k}`}>
            {[0, 1, 2].map((i) => <div key={i} className="flex items-center gap-2"><Skeleton className="h-7 w-7 rounded-full" /><Skeleton className="h-4 flex-1" /></div>)}
          </div>
        ) : error ? (
          <div className="p-4 text-center space-y-2" data-testid={`unassigned-ward-assign-error-${k}`}>
            <p className="text-xs text-destructive">{errMsg(error)}</p>
            <Button size="sm" variant="outline" onClick={() => { setData(null); setOpen(false); setTimeout(() => setOpen(true), 0); }}>Retry</Button>
          </div>
        ) : collectors.length === 0 ? (
          <div className="p-4 text-center space-y-2" data-testid={`unassigned-ward-assign-empty-${k}`}>
            <Users className="h-5 w-5 mx-auto text-muted-foreground" />
            <p className="text-sm font-medium">No tax collector belongs to {zoneLabel || "this zone"} yet</p>
            <p className="text-[11px] text-muted-foreground">Map a supervisor to this zone (their collectors become eligible) or add a collector under Users &amp; Staff.</p>
            <Button asChild size="sm" variant="outline" className="h-8">
              <Link to={`/${code}/admin/users${row.zone_id ? `?zone_id=${row.zone_id}` : ""}`} data-testid={`unassigned-ward-assign-goto-users-${k}`}>
                <Users className="h-3.5 w-3.5 mr-1" /> Open Users &amp; Staff
              </Link>
            </Button>
          </div>
        ) : (
          <Command className="rounded-none" data-testid={`unassigned-ward-assign-list-${k}`}>
            <CommandInput placeholder="Search collector, code or supervisor…" className="h-9 text-sm" data-testid={`unassigned-ward-assign-search-${k}`} />
            <CommandList className="max-h-[280px]">
              <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">No collector matches.</CommandEmpty>
              <CommandGroup heading={`${plural(collectors.length, "collector", "collectors")} in ${zoneLabel || "this zone"}`}>
                {collectors.map((c) => {
                  const covers = COVERS_META[c.covers];
                  const busy = busyId === c.id;
                  return (
                    <CommandItem key={c.id} value={`${c.name} ${c.employee_id || "no code"} ${c.supervisor_name || ""}`}
                      disabled={!!busyId && !busy} onSelect={() => assign(c)}
                      className="items-start gap-2 py-2 cursor-pointer aria-selected:bg-primary/5"
                      data-testid={`unassigned-ward-assign-option-${k}-${c.id}`} data-covers={c.covers}>
                      <StaffAvatar code={code} userId={c.id} name={c.name} size="xs" className="mt-0.5" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm leading-tight truncate">
                          {c.name}<EmpCode code={c.employee_id} />
                          {c.is_sub ? <span className="text-[11px] text-muted-foreground"> (sub)</span> : null}
                        </span>
                        <span className="block text-[11px] text-muted-foreground truncate">
                          {c.supervisor_name ? `Under ${staffLabelText(c.supervisor_name, c.supervisor_employee_id)}` : "No supervisor mapped"}
                          {" · "}{plural(c.ward_count, "ward", "wards")} · {plural(c.assigned_count, "property", "properties")}
                        </span>
                        {covers && <Badge variant="outline" className={`mt-1 text-[10px] py-0 font-normal ${covers.cls}`}>{covers.label}</Badge>}
                      </span>
                      {busy ? <Loader2 className="h-4 w-4 animate-spin text-primary mt-1 shrink-0" data-testid={`unassigned-ward-assign-busy-${k}`} />
                        : <UserPlus className="h-4 w-4 text-muted-foreground/60 mt-1 shrink-0" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
